
import { IDocumentDetails } from "../../@types/models";
import fetchClient from "./fetchClient";
import { getEnvironment360BizURL, getFetchApiOptions, ApiConfig } from '../configs/appConfig';
import { getHeaders } from "./dataService";
import { getMsalAccessToken } from "./msalUtil";
import { ISaveInfo, ISaveMailInfo, type IAccessProperties } from "../../Outlook/QuickSave/model/QuickSaveModel";
import { getMsalGraphScopes, getTargetInbox } from "./dataServiceOutlook";
import { getCurrentInternetMessageId } from "./OutlookMessageIdUtil";

export interface InsertableArchiveResult {
    Recno: number;
    Code: string;
    Description: string;
    CaseConnection: number;
    HasCcContacts: boolean;
}

export interface EmailDocumentCategoryResult {
    Recno: number;
    Code: string;
    Description: string;
}

export interface CaseResult {
    Recno: number;
    CaseNumber: string;
    Description: string;
    OrgUnit: string;
    ResponsiblePerson: string;
    StatusCode: string;
    StatusIsClosed: boolean;
}

export interface RecordTypeResult {
    Recno: number;
    Code: string;
    Description: string;
    DocumentCategory?: number;
    Display: string;
}

export interface CarbonCopyContactResult {
    Recno: number;
    Name: string;
    IsInternal: boolean;
    Domain: number;
    EmployerRecno?: number;
    EmployerName: string;
    Address: string;
    Zip: string;
}

export type { IAccessProperties } from "../../Outlook/QuickSave/model/QuickSaveModel";

export interface ValidateArchiveAndCaseResult {
    CaseIsValid: boolean;
    CaseValidationMessage: string | null;
    ArchiveIsValid: boolean;
    ArchiveValidationMessage: string | null;
    IsRecordType: boolean;
    AccessProperties: IAccessProperties | null;
}

export const saveMail = async (saveInfo: ISaveInfo, sCB: (resData: IDocumentDetails | null) => void, eCB: (err: string) => void) => {
    const targetInbox = await getTargetInbox();
    const token = await getMsalAccessToken(await getMsalGraphScopes());
    if (!token) { eCB("Failed to acquire access token"); return; }
    const itemId = await getCurrentInternetMessageId();
    if (!itemId) {
        eCB("Cannot retrieve mail ID for selected email");
        return;
    }
    const mailInfo: ISaveMailInfo = {
        accessToken: token,
        itemId,
        targetInbox: targetInbox ?? null,
        ...saveInfo
    };

    const headers = await getHeaders();
    try {
        const response = await fetchClient(getEnvironment360BizURL() +
            ApiConfig.SaveMail, {
            body: JSON.stringify(mailInfo),
            headers: headers,
            method: "POST",
            ...getFetchApiOptions()
        }) as IDocumentDetails | null;
        sCB(response);
    } catch (err) {
        eCB("Error archiving mail: " + String(err));
    }
};

export const getInsertableArchives = async (): Promise<InsertableArchiveResult[]> => {
    const headers = await getHeaders();
    try {
        return await fetchClient(getEnvironment360BizURL() +
            ApiConfig.GetInsertableArchives, {
            headers: headers,
            method: "POST",
            ...getFetchApiOptions()
        }) as InsertableArchiveResult[];

    } catch (err) {
        throw new Error("Error fetching insertable archives: " + String(err));
    }

};

export const getEmailDocumentCategories = async (archiveRecno: number): Promise<EmailDocumentCategoryResult[]> => {
    const headers = await getHeaders();
    try {
        return await fetchClient(getEnvironment360BizURL() +
            ApiConfig.GetEmailDocumentCategories, {
            body: JSON.stringify({ archiveRecno: archiveRecno }),
            headers: headers,
            method: "POST",
            ...getFetchApiOptions()
        }) as EmailDocumentCategoryResult[];

    } catch (err) {
        throw new Error("Error getting email document categories: " + String(err));
    }
};

export const searchCasePrepopulate = async (): Promise<CaseResult[]> => {
    const headers = await getHeaders();
    try {
        return await fetchClient(getEnvironment360BizURL() +
            ApiConfig.SearchCasePrepopulate, {
            headers: headers,
            method: "POST",
            ...getFetchApiOptions()
        }) as CaseResult[];

    } catch (err) {
        throw new Error("Error searching case prepopulate: " + String(err));
    }
};

export const searchCaseForNewDocument = async (searchText: string, archiveRecno: number): Promise<CaseResult[]> => {
    const headers = await getHeaders();
    try {
        return await fetchClient(getEnvironment360BizURL() +
            ApiConfig.SearchCaseForNewDocument, {
            body: JSON.stringify({ searchText: searchText, documentArchive: archiveRecno }),
            headers: headers,
            method: "POST",
            ...getFetchApiOptions()
        }) as CaseResult[];

    } catch (err) {
        throw new Error("Error searching cases for new document: " + String(err));
    }
};

export const getRecordTypes = async (caseRecno: number, documentArchive: number): Promise<RecordTypeResult[]> => {
    const headers = await getHeaders();
    try {
        return await fetchClient(getEnvironment360BizURL() +
            ApiConfig.GetRecordTypes, {
            body: JSON.stringify({ caseRecno: caseRecno, documentArchive: documentArchive }),
            headers: headers,
            method: "POST",
            ...getFetchApiOptions()
        }) as RecordTypeResult[];

    } catch (err) {
        throw new Error("Error getting record types: " + String(err));
    }
};

export const carbonCopyContactPrepopulate = async (caseNumber: number): Promise<CarbonCopyContactResult[]> => {
    const headers = await getHeaders();
    try {
        return await fetchClient(getEnvironment360BizURL() +
            ApiConfig.CarbonCopyContactPrepopulate, {
            headers: headers,
            body: JSON.stringify({ caseRecno: caseNumber, internalContacts: "-1", domains: [1, 2] }),
            method: "POST",
            ...getFetchApiOptions()
        }) as CarbonCopyContactResult[];

    } catch (err) {
        throw new Error("Error prepopulating carbon copy contacts: " + String(err));
    }
};
export const searchCarbonCopyContacts = async (searchText: string): Promise<CarbonCopyContactResult[]> => {
    const headers = await getHeaders();
    try {
        return await fetchClient(getEnvironment360BizURL() +
            ApiConfig.SearchCarbonCopyContacts, {
            headers: headers,
            body: JSON.stringify({ searchText: searchText, internalContacts: "-1", domains: [1, 2] }),
            method: "POST",
            ...getFetchApiOptions()
        }) as CarbonCopyContactResult[];

    } catch (err) {
        throw new Error("Error searching carbon copy contacts: " + String(err));
    }
};

export const validateCase = async (documentArchive: number, caseRecno: number): Promise<ValidateArchiveAndCaseResult> => {
    const headers = await getHeaders();
    try {
        return await fetchClient(getEnvironment360BizURL() +
            ApiConfig.ValidateCase, {
            body: JSON.stringify({ documentArchive: documentArchive, caseRecno: caseRecno }),
            headers: headers,
            method: "POST",
            ...getFetchApiOptions()
        }) as ValidateArchiveAndCaseResult;

    } catch (err) {
        throw new Error("Error validating case: " + String(err));
    }
};